import { FilterProps } from "@components/job_search/filters/filter_props.ts";
import useDevice from "@hooks/use_device.ts";
import { DesktopMultiSelectFilter } from "@components/job_search/filters/desktop_multi_select_filter.tsx";
import { MobileMultiSelectFilter } from "@components/job_search/filters/mobile_multi_select_filter.tsx";
import type { Option } from "@sprout/checkbox.tsx";
import { useMemo } from "react";

export interface MultiSelectFilterProps extends FilterProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
}

export type SpecializedMultiSelectFilterProps = {
  buttonText: string;
  options: Option[];
  onSelect: (selectedOptions: Option | Option[]) => void;
} & Omit<MultiSelectFilterProps, "options" | "onValuesChange">;

export const MultiSelectFilter = ({
  options,
  onValuesChange,
  ...props
}: MultiSelectFilterProps) => {
  const device = useDevice();

  const buttonText = useMemo(() => {
    if (props.values.length === 0) {
      return props.name;
    }

    const displayValue =
      props.values.length > 1
        ? props.values.length
        : options.find((option) => option.value === props.values[0])?.label;

    return `${props.name} (${displayValue})`;
  }, [props.name, props.values]);

  const handleSelect = (selectedOptions: Option | Option[]) => {
    if (!Array.isArray(selectedOptions)) {
      console.warn("handleSelect received a non-array argument. This will result in a no-op.");
      return;
    }

    const selectedValues = selectedOptions.map((option) => option.value as string);

    onValuesChange(selectedValues);
  };

  if (device.isMobile) {
    return (
      <MobileMultiSelectFilter
        buttonText={buttonText}
        options={options}
        onSelect={handleSelect}
        {...props}
      />
    );
  }

  return (
    <DesktopMultiSelectFilter
      buttonText={buttonText}
      options={options}
      onSelect={handleSelect}
      {...props}
    />
  );
};
